import React from "react";

const portfolioData = [
  { month: "Jan", value: 41800 },
  { month: "Feb", value: 43200 },
  { month: "Mar", value: 43800 },
  { month: "Apr", value: 43700 },
  { month: "May", value: 45100 },
  { month: "Jun", value: 46300 },
  { month: "Jul", value: 47200 },
  { month: "Aug", value: 48200 },
  { month: "Sep", value: 49700 },
  { month: "Oct", value: 51200 },
  { month: "Nov", value: 52800 },
  { month: "Dec", value: 53760 },
];

const allocationData = [
  {
    name: "Residential",
    percentage: 42,
  },
  {
    name: "Multifamily",
    percentage: 31,
  },
  {
    name: "Commercial",
    percentage: 27,
  },
];

const PortfolioOverview = () => {
  /*
   * SVG graph points
   */
  const graphWidth = 700;
  const graphHeight = 230;

  const minValue = Math.min(...portfolioData.map((item) => item.value));
  const maxValue = Math.max(...portfolioData.map((item) => item.value));

  const points = portfolioData.map((item, index) => {
    const x =
      (index / (portfolioData.length - 1)) *
      (graphWidth - 40);

    const normalized =
      (item.value - minValue) /
      (maxValue - minValue);

    const y =
      graphHeight -
      normalized * (graphHeight - 55) -
      20;

    return {
      ...item,
      x: x + 20,
      y,
    };
  });

  const linePath = points
    .map((point, index) => {
      return `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`;
    })
    .join(" ");

  const areaPath = `
    ${linePath}
    L ${points[points.length - 1].x} ${graphHeight}
    L ${points[0].x} ${graphHeight}
    Z
  `;

  return (
    <section
      id="portfolio"
      className="
        relative
        mt-20
        overflow-hidden
        bg-[#fdfbf5]
        px-4
        py-16
        sm:px-6
        sm:py-20
        lg:px-8
        lg:py-24
      "
    >
      {/* =====================================================
          SAME BLURRED BACKGROUND LANGUAGE
      ===================================================== */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">

        <div
          className="
            absolute
            -left-40
            top-10
            h-[400px]
            w-[400px]
            rounded-full
            bg-[#dfe8cf]/50
            blur-[110px]
          "
        />

        <div
          className="
            absolute
            right-[-180px]
            top-[30%]
            h-[500px]
            w-[500px]
            rounded-full
            bg-[#e8ecd9]/60
            blur-[120px]
          "
        />

        <div
          className="
            absolute
            bottom-[-220px]
            left-[35%]
            h-[450px]
            w-[450px]
            rounded-full
            bg-[#e5ead7]/45
            blur-[120px]
          "
        />

      </div>

      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}

      <div className="relative z-10 mx-auto w-full max-w-[1800px]">

        {/* ================= HEADER ================= */}

        <div
          className="
            mb-9
            grid
            grid-cols-1
            items-end
            gap-5
            sm:mb-11
            md:grid-cols-[1fr_0.72fr]
          "
        >

          <div>

            <p
              className="
                mb-3
                text-[10px]
                font-bold
                uppercase
                tracking-[0.18em]
                text-[#4c7a2e]
                sm:text-[11px]
              "
            >
              Your portfolio
            </p>

            <h2
              className="
                max-w-[650px]
                text-[31px]
                font-extrabold
                leading-[1.1]
                tracking-[-0.04em]
                text-[#23241c]
                sm:text-[38px]
                lg:text-[44px]
              "
            >
              One dashboard for everything
              <br />
              you own
            </h2>

            <div
              className="
                mt-4
                h-1
                w-12
                rounded-full
                bg-gradient-to-r
                from-[#375c20]
                to-[#6fa142]
              "
            />

          </div>

          <p
            className="
              max-w-[370px]
              pb-1
              text-[11px]
              font-medium
              leading-[1.8]
              text-[#6f765f]
              sm:text-[12px]
            "
          >
            Track performance across every property, in real time,
            from any device.
          </p>

        </div>

        {/* =====================================================
            DASHBOARD CARD
        ===================================================== */}

        <div
          className="
            relative
            overflow-hidden
            rounded-[24px]
            border
            border-white/90
            bg-white/75
            p-4
            shadow-[0_22px_60px_rgba(55,92,32,0.10)]
            backdrop-blur-xl
            sm:p-6
            lg:p-7
          "
        >

          {/* subtle inner glow */}

          <div
            className="
              pointer-events-none
              absolute
              -right-20
              -top-20
              h-64
              w-64
              rounded-full
              bg-[#dfe8cf]/35
              blur-[80px]
            "
          />

          <div className="relative z-10">

            {/* ================= METRICS ================= */}

            <div
              className="
                grid
                grid-cols-1
                gap-3
                sm:grid-cols-2
                lg:grid-cols-4
              "
            >

              {/* Total invested */}

              <div
                className="
                  rounded-[14px]
                  border
                  border-[#dfe5d3]
                  bg-[#fbfaf4]
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:border-[#c9d7b8]
                  hover:shadow-[0_10px_25px_rgba(55,92,32,0.08)]
                "
              >
                <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#7c8468]">
                  Total invested
                </p>

                <p className="mt-2 text-[20px] font-bold tracking-[-0.025em] text-[#23241c]">
                  AED 48,200
                </p>

                <p className="mt-1 text-[9px] font-medium text-[#4c7a2e]">
                  +AED 4,100 since last quarter
                </p>
              </div>

              {/* Portfolio value */}

              <div
                className="
                  rounded-[14px]
                  border
                  border-[#dfe5d3]
                  bg-[#fbfaf4]
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:border-[#c9d7b8]
                  hover:shadow-[0_10px_25px_rgba(55,92,32,0.08)]
                "
              >
                <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#7c8468]">
                  Portfolio value
                </p>

                <p className="mt-2 text-[20px] font-bold tracking-[-0.025em] text-[#23241c]">
                  AED 53,760
                </p>

                <p className="mt-1 text-[9px] font-medium text-[#4c7a2e]">
                  +11.5% overall
                </p>
              </div>

              {/* Annual return */}

              <div
                className="
                  rounded-[14px]
                  border
                  border-[#dfe5d3]
                  bg-[#fbfaf4]
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:border-[#c9d7b8]
                  hover:shadow-[0_10px_25px_rgba(55,92,32,0.08)]
                "
              >
                <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#7c8468]">
                  Annual return
                </p>

                <p className="mt-2 text-[20px] font-bold tracking-[-0.025em] text-[#23241c]">
                  8.7%
                </p>

                <p className="mt-1 text-[9px] font-medium text-[#7c8468]">
                  vs. 6.2% avg. REIT
                </p>
              </div>

              {/* Monthly income */}

              <div
                className="
                  rounded-[14px]
                  border
                  border-[#dfe5d3]
                  bg-[#fbfaf4]
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:border-[#c9d7b8]
                  hover:shadow-[0_10px_25px_rgba(55,92,32,0.08)]
                "
              >
                <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-[#7c8468]">
                  Monthly income
                </p>

                <p className="mt-2 text-[20px] font-bold tracking-[-0.025em] text-[#23241c]">
                  AED 412
                </p>

                <p className="mt-1 text-[9px] font-medium text-[#4c7a2e]">
                  Paid on the 1st
                </p>
              </div>

            </div>

            {/* =================================================
                GRAPH + DONUT
            ================================================= */}

            <div
              className="
                mt-7
                grid
                grid-cols-1
                gap-8
                lg:grid-cols-[1.65fr_0.85fr]
              "
            >

              {/* ================= LINE GRAPH ================= */}

              <div>

                <div className="mb-4 flex items-center justify-between">

                  <div>
                    <p
                      className="
                        text-[10px]
                        font-bold
                        uppercase
                        tracking-[0.12em]
                        text-[#6f765f]
                      "
                    >
                      Portfolio value
                    </p>

                    <p className="mt-1 text-[9px] font-medium text-[#9a9d88]">
                      Last 12 months
                    </p>
                  </div>

                  <div
                    className="
                      rounded-full
                      border
                      border-[#dfe5d3]
                      bg-white
                      px-3
                      py-1.5
                      text-[9px]
                      font-semibold
                      text-[#4c7a2e]
                      shadow-sm
                    "
                  >
                    +11.5%
                  </div>

                </div>

                <div
                  className="
                    overflow-hidden
                    rounded-[16px]
                    border
                    border-[#e1e5d7]
                    bg-[#fdfbf5]
                    p-3
                    sm:p-4
                  "
                >

                  <svg
                    viewBox={`0 0 ${graphWidth} ${graphHeight}`}
                    className="h-[220px] w-full"
                    preserveAspectRatio="none"
                  >

                    {/* horizontal grid */}

                    {[0, 1, 2, 3].map((line) => {
                      const y =
                        40 +
                        line * 48;

                      return (
                        <line
                          key={line}
                          x1="20"
                          x2="680"
                          y1={y}
                          y2={y}
                          stroke="#e4e6dc"
                          strokeWidth="1"
                        />
                      );
                    })}

                    {/* area */}

                    <defs>
                      <linearGradient
                        id="portfolioArea"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#4c7a2e"
                          stopOpacity="0.20"
                        />

                        <stop
                          offset="100%"
                          stopColor="#4c7a2e"
                          stopOpacity="0.01"
                        />
                      </linearGradient>
                    </defs>

                    <path
                      d={areaPath}
                      fill="url(#portfolioArea)"
                    />

                    {/* graph line */}

                    <path
                      d={linePath}
                      fill="none"
                      stroke="#4c7a2e"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* points */}

                    {points.map((point, index) => (
                      <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r={index === points.length - 1 ? 5 : 3}
                        fill="white"
                        stroke="#4c7a2e"
                        strokeWidth="2"
                      />
                    ))}

                  </svg>

                  {/* X axis */}

                  <div
                    className="
                      mt-1
                      grid
                      grid-cols-6
                      text-[8px]
                      font-medium
                      text-[#9a9d88]
                    "
                  >
                    <span>Jan</span>
                    <span>Mar</span>
                    <span>May</span>
                    <span>Jul</span>
                    <span>Sep</span>
                    <span className="text-right">Dec</span>
                  </div>

                </div>

              </div>

              {/* ================= ALLOCATION ================= */}

              <div>

                <p
                  className="
                    text-[10px]
                    font-bold
                    uppercase
                    tracking-[0.12em]
                    text-[#6f765f]
                  "
                >
                  Allocation by type
                </p>

                <div
                  className="
                    mt-4
                    flex
                    flex-col
                    items-center
                    justify-center
                    rounded-[16px]
                    border
                    border-[#e1e5d7]
                    bg-[#fdfbf5]
                    px-4
                    py-5
                  "
                >

                  {/* DONUT */}

                  <div className="relative h-[150px] w-[150px]">

                    <svg
                      viewBox="0 0 120 120"
                      className="h-full w-full -rotate-90"
                    >

                      {/* background */}

                      <circle
                        cx="60"
                        cy="60"
                        r="43"
                        fill="none"
                        stroke="#e6e8de"
                        strokeWidth="13"
                      />

                      {/* residential */}

                      <circle
                        cx="60"
                        cy="60"
                        r="43"
                        fill="none"
                        stroke="#4c7a2e"
                        strokeWidth="13"
                        strokeDasharray="113.4 270.2"
                        strokeDashoffset="0"
                      />

                      {/* multifamily */}

                      <circle
                        cx="60"
                        cy="60"
                        r="43"
                        fill="none"
                        stroke="#6f8f4f"
                        strokeWidth="13"
                        strokeDasharray="83.7 270.2"
                        strokeDashoffset="-113.4"
                      />

                      {/* commercial */}

                      <circle
                        cx="60"
                        cy="60"
                        r="43"
                        fill="none"
                        stroke="#a18b55"
                        strokeWidth="13"
                        strokeDasharray="72.9 270.2"
                        strokeDashoffset="-197.1"
                      />

                    </svg>

                    {/* center */}

                    <div
                      className="
                        absolute
                        inset-0
                        flex
                        flex-col
                        items-center
                        justify-center
                      "
                    >
                      <span className="text-[10px] font-medium text-[#7c8468]">
                        Total
                      </span>

                      <strong className="mt-0.5 text-[19px] font-bold text-[#23241c]">
                        100%
                      </strong>
                    </div>

                  </div>

                  {/* LEGEND */}

                  <div className="mt-5 w-full space-y-3">

                    <div className="flex items-center justify-between">

                      <div className="flex items-center gap-2">

                        <span className="h-2.5 w-2.5 rounded-full bg-[#4c7a2e]" />

                        <span className="text-[10px] font-medium text-[#666d56]">
                          Residential
                        </span>

                      </div>

                      <span className="text-[10px] font-bold text-[#23241c]">
                        42%
                      </span>

                    </div>

                    <div className="flex items-center justify-between">

                      <div className="flex items-center gap-2">

                        <span className="h-2.5 w-2.5 rounded-full bg-[#6f8f4f]" />

                        <span className="text-[10px] font-medium text-[#666d56]">
                          Multifamily
                        </span>

                      </div>

                      <span className="text-[10px] font-bold text-[#23241c]">
                        31%
                      </span>

                    </div>

                    <div className="flex items-center justify-between">

                      <div className="flex items-center gap-2">

                        <span className="h-2.5 w-2.5 rounded-full bg-[#a18b55]" />

                        <span className="text-[10px] font-medium text-[#666d56]">
                          Commercial
                        </span>

                      </div>

                      <span className="text-[10px] font-bold text-[#23241c]">
                        27%
                      </span>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default PortfolioOverview;

